/**
 * Unified console logging for the Google Ad Manager (GAM) integration, intended
 * to be forwarded to the ad agency. Every GAM-related line goes through this
 * helper so the output has a single, greppable shape:
 *
 *   [GAM HH:MM:SS.mmm +<elapsedMs>ms] <event> { slot:'gam-<id>', ...payload }
 *
 * - The `[GAM` prefix lets the agency filter the browser console (or a
 *   "Save as…" log export) down to just the ad lines.
 * - The wall-clock time plus the `+<elapsedMs>` delta (measured from the first
 *   GAM log of the session) makes ordering and race conditions visible —
 *   e.g. consent arriving vs. the loader library becoming ready.
 * - Every line carries the `slot` key (per-component identifier) so multiple
 *   ad slots on one page can be told apart.
 *
 * Event names are short kebab-case codes. Their meaning is documented for the
 * agency in docs/gam-debug-log-legend.md (kept out of the console output).
 *
 * Logging is OFF by default and gated behind a feature flag, so production
 * consoles stay clean. Enable it either by setting `window.__gamDebug = true`
 * before the flow, or by adding `?gamDebug=1` (or `?gamDebug=true`) to the URL.
 */

/* eslint-disable no-console -- this debug helper exists to write to the console */

let firstLogAt: number | null = null;

// Resolved on EVERY log call (not cached): the flag is most often flipped from
// the console (`window.__gamDebug = true`) AFTER the page has already emitted a
// log line, so caching the first result would freeze it on `false` and the
// console toggle would never take effect. The check is a couple of cheap
// property reads, so re-evaluating per call is fine.
function resolveEnabled (): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (window.__gamDebug === true) {
    return true;
  }
  try {
    const value = new URLSearchParams(window.location.search).get('gamDebug');
    if (value === '1' || value === 'true') {
      // Persist on the window so the flag survives SPA navigations that drop
      // the query string.
      window.__gamDebug = true;
      return true;
    }
  } catch {
    // location/URLSearchParams unavailable — treat as disabled.
  }
  return false;
}

function pad (value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

function formatClock (date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
}

/**
 * Emit a single unified GAM debug line.
 *
 * @param event   Stable, kebab-case event name (see docs/gam-debug-log-legend.md).
 * @param slot    Per-slot identifier from gamDebugKey() (e.g. 'gam-123'),
 *                or 'plugin' for the SPA-navigation plugin.
 * @param payload Optional structured data for this event.
 */
export function gamDebugLog (event: string, slot: string, payload?: Record<string, unknown>): void {
  if (!resolveEnabled()) {
    return;
  }
  const now = new Date();
  const nowMs = now.getTime();
  if (firstLogAt === null) {
    firstLogAt = nowMs;
  }
  const elapsedMs = nowMs - firstLogAt;
  const prefix = `[GAM ${formatClock(now)} +${elapsedMs}ms] ${event}`;
  const data = (payload && Object.keys(payload).length > 0) ? { slot, ...payload } : { slot };
  // Also append a JSON string of the data: nested arrays/objects (e.g. a
  // slot-state `slots:[...]`) otherwise collapse to `Array(3)` in a console
  // "Save as…" export, losing exactly the detail the agency needs.
  let dataJson: string;
  try {
    dataJson = JSON.stringify(data);
  } catch {
    dataJson = '[unserializable]';
  }
  console.log(prefix, data, dataJson);
}
